import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
} from "firebase/messaging";
import { vapidKey } from "../../util/FirebaseConfig";
import { schedulerApi } from "../api";
import { firebaseApp as sharedFirebaseApp } from "./firebaseApp";

/**
 * Enhanced Firebase Service
 *
 * Provides centralized Firebase functionality including:
 * - Firebase app initialization
 * - Push notification management
 * - Service worker registration
 * - Token management
 * - Message handling
 *
 * @class FirebaseService
 */
class FirebaseService {
  constructor() {
    this.firebaseApp = null;
    this.messaging = null;
    this.isElectron =
      typeof window !== "undefined" && !!window.electronPushReceiver;
    this.isSupported = false;
    this.initializationPromise = null;
    this._onMessageUnsub = null;
    this._onMessageActive = false;
    this.initializationPromise = this.initialize();
  }

  async ensureInitialized() {
    if (this.initializationPromise) {
      await this.initializationPromise;
    }
  }
  /**
   * Initializes Firebase and messaging
   * @private
   */
  async initialize() {
    try {
      // Use the shared Firebase app instance
      this.firebaseApp = sharedFirebaseApp;

      // Only initialize messaging if supported and not in Electron
      if (!this.isElectron && !this.isIOS() && !this.isSafari()) {
        const messagingSupported = await isSupported();

        if (messagingSupported) {
          this.messaging = getMessaging(this.firebaseApp);
          this.isSupported = true;
        } else {
          console.warn("Firebase Messaging is not supported in this browser");
        }
      }
    } catch (error) {
      console.error(" Firebase initialization failed:", error);
    }
  }
  /**
   * Checks if the current platform is iOS
   * @returns {boolean} True if iOS device
   */
  isIOS() {
    const iOSPlatforms = [
      "iPad Simulator",
      "iPhone Simulator",
      "iPod Simulator",
      "iPad",
      "iPhone",
      "iPod",
    ];

    return (
      iOSPlatforms.includes(navigator.platform) ||
      (navigator.userAgent.includes("Mac") && "ontouchend" in document)
    );
  }

  /**
   * Checks if the current browser is Safari
   * @returns {boolean} True if Safari browser
   */
  isSafari() {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }

  /**
   * Registers or gets existing service worker
   * @returns {Promise<ServiceWorkerRegistration>} Service worker registration
   * @throws {Error} If service workers are not supported
   */
  async getOrRegisterServiceWorker() {
    if (this.isElectron || !("serviceWorker" in navigator)) {
      throw new Error("Service workers not applicable in this environment");
    }

    try {
      // Check for existing registration
      const existingRegistration =
        await navigator.serviceWorker.getRegistration("/");

      if (existingRegistration) {
        return existingRegistration;
      }

      // Register new service worker
      return await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js",
        {
          scope: "/",
        },
      );
    } catch (error) {
      console.error(" Service worker registration failed:", error);
      throw error;
    }
  }

  /**
   * Fetches FCM token and sends it to backend
   * @param {Function} setTokenFound - Callback to set token found status
   * @returns {Promise<string|null>} FCM token or null if failed
   */
  async fetchAndRegisterToken(setTokenFound) {
    // Wait for initialization to complete
    await this.ensureInitialized();

    if (this.isElectron) {
      console.warn(
        " Attempted to fetch web FCM token in Electron environment. Skipping.",
      );
      setTokenFound(false);
      return null;
    }

    if (!this.isSupported || !this.messaging) {
      console.warn("Firebase messaging not supported or initialized");
      setTokenFound(false);
      return null;
    }

    try {
      const serviceWorkerRegistration = await this.getOrRegisterServiceWorker();

      // Wait for service worker to become active
      await this.waitForServiceWorkerToBeActive(serviceWorkerRegistration);

      const currentToken = await getToken(this.messaging, {
        vapidKey: vapidKey,
        serviceWorkerRegistration,
      });

      if (currentToken) {
        setTokenFound(true);

        // Send token to backend using API service (with cookies via withCredentials)
        await schedulerApi.updateFirebaseToken(currentToken, null);

        return currentToken;
      } else {
        setTokenFound(false);
        return null;
      }
    } catch (error) {
      console.error(" Error fetching FCM token:", error);
      setTokenFound(false);
      return null;
    }
  }

  /**
   * Waits for service worker to become active
   * @param {ServiceWorkerRegistration} registration - Service worker registration
   * @returns {Promise<void>}
   */
  async waitForServiceWorkerToBeActive(registration) {
    return new Promise((resolve) => {
      if (registration.active) {
        resolve();
        return;
      }

      // If installing, wait for it to become active
      if (registration.installing) {
        registration.installing.addEventListener("statechange", function () {
          if (this.state === "activated") {
            resolve();
          }
        });
      }
      // If waiting, make it active
      else if (registration.waiting) {
        registration.waiting.addEventListener("statechange", function () {
          if (this.state === "activated") {
            resolve();
          }
        });
      }
      // Fallback: wait a bit and resolve
      else {
        setTimeout(resolve, 1000);
      }
    });
  }

  /**
   * Sends Safari push alert token to backend
   * @param {string} pushAlertSubscriberId - Safari push alert subscriber ID
   * @returns {Promise<void>}
   */
  async registerSafariToken(pushAlertSubscriberId) {
    try {
      await schedulerApi.updateFirebaseToken(null, pushAlertSubscriberId);
    } catch (error) {
      console.error("Error sending Safari push alert token:", error);
      throw error;
    }
  }

  /**
   * Sets up foreground message listener
   * @param {Function} callback - The callback to execute when a message is received
   * @returns {Promise<Function>} A Promise that resolves to an unsubscribe function
   */
  async onMessageListener(callback) {
    await this.ensureInitialized();

    if (this.isElectron) {
      return () => {};
    }

    if (!this.isSupported || !this.messaging) {
      return () => {};
    }

    // Guard against duplicate registration
    if (this._onMessageActive) {
      return this._onMessageUnsub || (() => {});
    }

    this._onMessageActive = true;
    const unsub = onMessage(this.messaging, (payload) => {
      callback(payload);
    });

    this._onMessageUnsub = () => {
      try {
        unsub();
      } catch (error) {
        console.error(
          "Error unsubscribing from Firebase message listener:",
          error,
        );
      }
      this._onMessageActive = false;
      this._onMessageUnsub = null;
    };

    return this._onMessageUnsub;
  }
  /**
   * Requests notification permission and fetches token
   * @param {Function} setTokenFound - Callback to set token found status
   * @returns {Promise<boolean>} True if permission granted and token retrieved
   */
  async requestPermissionAndGetToken(setTokenFound) {
    // Wait for initialization to complete
    await this.ensureInitialized();

    if (this.isElectron) {
      setTokenFound(false);
      return false;
    }

    try {
      const permission = await Notification.requestPermission();

      if (permission === "granted") {
        const token = await this.fetchAndRegisterToken(setTokenFound);
        return !!token;
      } else {
        setTokenFound(false);
        return false;
      }
    } catch (error) {
      console.error(" Error requesting notification permission:", error);
      setTokenFound(false);
      return false;
    }
  }

  /**
   * Handles incoming foreground messages
   * @param {Object} payload - Message payload
   * @param {Function} onNotificationReceived - Callback for notification handling
   * @returns {Promise<void>}
   */
  async handleForegroundMessage(payload, onNotificationReceived) {
    const notificationData = {
      title: payload.data?.title || "New Notification",
      body: payload.data?.body || "",
      icon: payload.data?.icon,
      url: payload.data?.url,
    };

    if (onNotificationReceived) {
      onNotificationReceived(notificationData);
    }

    // Show Chrome notification via service worker
    if ("serviceWorker" in navigator) {
      try {
        const registration = await navigator.serviceWorker.getRegistration("/");
        if (registration?.active) {
          registration.active.postMessage({
            type: "SHOW_NOTIFICATION",
            payload: payload,
          });
        }
      } catch (error) {
        console.error("Error posting to service worker:", error);
      }
    }
  }
  /**
   * Gets current notification permission status
   * @returns {string} Permission status ('granted', 'denied', 'default')
   */
  getNotificationPermission() {
    if (!("Notification" in window)) {
      return "not-supported";
    }
    return Notification.permission;
  }

  /**
   * Checks if push notifications are supported in current environment
   * @returns {boolean} True if push notifications are supported
   */
  isPushNotificationSupported() {
    return (
      !this.isElectron &&
      this.isSupported &&
      "Notification" in window &&
      "serviceWorker" in navigator
    );
  }

  /**
   * Gets Firebase app instance
   * @returns {FirebaseApp|null} Firebase app instance
   */
  getFirebaseApp() {
    return this.firebaseApp;
  }

  /**
   * Gets messaging instance
   * @returns {Messaging|null} Firebase messaging instance
   */
  getMessaging() {
    return this.messaging;
  }

  /**
   * Cleanup method for removing listeners and cleaning up resources
   */
  cleanup() {
    // Add any cleanup logic here if needed
    try {
      this._onMessageUnsub?.();
    } catch {}
  }
}

// Create and export singleton instance
const firebaseService = new FirebaseService();
export default firebaseService;
